import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Fragment } from "react";

import { Alert } from "components/common/Alert";
import { Input } from "components/common/Input";
import { ValidateButton } from "components/common/ValidateButton";
import ScanQRCodeButton from "components/qrCodeScanner/ScanQRCodeButton";
import { FieldType } from "types/scenarioType";
import { Language, LanguageMap } from "Language";

type Props = {
    fields: FieldType[];
    onSubmit: (data: any) => Promise<void>;
    formMessage: string;
    formButtonText: string;
    successMessage?: string | null;
    errorMessage?: string | null;
};

const Form = ({
    fields,
    onSubmit,
    formMessage,
    formButtonText,
    successMessage,
    errorMessage,
}: Props) => {
    const {
        register,
        setValue,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
    } = useForm({
        mode: "all",
    });

    const { t, i18n } = useTranslation();

    const getFieldPlaceholder = (field: FieldType): string => {
        const key = Object.keys(field.translations).find((k) =>
            k.toLowerCase().startsWith(i18n.language),
        );

        let translation: string = key
            ? field.translations[key]
            : field.translations[LanguageMap[Language.EN]] ||
              Object.values(field.translations ?? {})[0] ||
              "";

        if (field.mandatory) {
            translation += "(*)";
        }

        return translation;
    };

    const getFieldType = (field: FieldType): string => {
        if (field.validator === "email") return "email";
        if (field.type === "string") return "text";
        return field.type;
    };

    const getField = (field: FieldType, index: number) => {
        return (
            <Fragment key={field.name ?? index}>
                <InputWithQRCodeWrapper>
                    <StyledInput
                        {...register(field.name, { required: field.mandatory })}
                        type={getFieldType(field)}
                        $error={Boolean(errors[field.name])}
                        $showSendPINByEmail={Boolean(field.showSendPINByEmail)}
                        placeholder={getFieldPlaceholder(field)}
                        aria-invalid={errors[field.name] ? "true" : "false"}
                        key={index}
                        style={{
                            paddingRight: field.showQRCode ? "35px" : "12px",
                        }}
                    />
                    {field.showQRCode && (
                        <QRCodeButtonWrapper>
                            <ScanQRCodeButton
                                setValue={(value: string) => {
                                    setValue(field.name, value);
                                }}
                            />
                        </QRCodeButtonWrapper>
                    )}
                </InputWithQRCodeWrapper>
                {field.showSendPINByEmail && (
                    <ResendPinContainer>
                        <ResendPinLink onClick={field.onResendPINClick}>
                            {`${t("resendPinByEmail")}`}
                        </ResendPinLink>
                    </ResendPinContainer>
                )}
            </Fragment>
        );
    };

    return (
        <FormWrapper>
            {fields[0].name === "idReference" ? (
                <StyledMessage>{formMessage}</StyledMessage>
            ) : null}
            {successMessage && <AlertSuccess>{successMessage}</AlertSuccess>}
            {errorMessage && <Alert>{errorMessage}</Alert>}
            <form onSubmit={handleSubmit(onSubmit)}>
                {fields.map((field, index) => getField(field, index))}
                <StyledButton
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    $isValid={isValid}
                >
                    {formButtonText}
                </StyledButton>
            </form>
        </FormWrapper>
    );
};

const StyledInput = styled(Input)<{
    $error: boolean;
    $showSendPINByEmail: boolean;
}>`
    &:focus {
        border-color: ${(props) => (props.$error ? " #dc3545" : null)};
        box-shadow: ${(props) =>
            props.$error ? " 0 0 0 0.2rem rgb(220 53 69 / 25%);" : null};
    }
    border-color: ${(props) => (props.$error ? " #dc3545" : null)};
    box-shadow: ${(props) =>
        props.$error ? " 0 0 0 0.2rem rgb(220 53 69 / 25%);" : null};
    margin-bottom: ${(props) => (props.$showSendPINByEmail ? "0px" : "15px")};
`;

const FormWrapper = styled.div`
    padding: 1.5rem;
`;

const StyledMessage = styled.p`
    margin: 0;
    padding-bottom: 1em;
    font-size: 14px;
    line-height: 1.5rem;
`;

const StyledButton = styled(ValidateButton)<{
    $isValid: boolean;
}>`
    cursor: ${(props) => (props.$isValid ? "pointer" : "default")};

    &:disabled {
        pointer-events: none;
    }
`;

const ResendPinContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 15px;
`;

const ResendPinLink = styled.a`
    font-size: 0.75rem;
    line-height: 22px;
    text-decoration: underline;
    cursor: pointer;
    color: gray;
`;

const InputWithQRCodeWrapper = styled.div`
    position: relative;
    display: flex;
    alignitems: center;
`;

const QRCodeButtonWrapper = styled.div`
    position: absolute;
    right: 0;
    top: 0;
`;

const AlertSuccess = styled(Alert)`
    color: #3c763d;
    background-color: #dff0d8;
`;

export default Form;
